import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";

interface ScreenShotWrapperProps {
  children: (targetRef: React.RefObject<HTMLDivElement>) => React.ReactNode;
  studentName?: string;
  reportType: string;
}

const convertImgToBase64 = (src: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    const separator = src.includes("?") ? "&" : "?";
    img.src = `${src}${separator}not-cached=${Date.now()}`;

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
          return;
        }
      } catch {
        // Handled below
      }
      resolve(src);
    };

    img.onerror = () => {
      resolve(src);
    };
  });
};

const ScreenShotWrapper = forwardRef<any, ScreenShotWrapperProps>(
  ({ children, studentName = "Student", reportType }, ref) => {
    const targetViewRef = useRef<HTMLDivElement>(null);
    const [pdfGenerating, setPdfGenerating] = useState(false);

    const convertAllImagesToBase64 = async (container: HTMLElement) => {
      const images = Array.from(container.querySelectorAll("img"));

      await Promise.all(
        images.map(async (img) => {
          if (img.src.startsWith("http://") || img.src.startsWith("https://")) {
            const base64 = await convertImgToBase64(img.src);
            if (base64.startsWith("data:image")) {
              img.src = base64;
            }
          }
        }),
      );
    };

    useImperativeHandle(ref, () => ({
      generatePDF: async () => {
        if (pdfGenerating) return;

        try {
          setPdfGenerating(true);

          const element = targetViewRef.current;
          if (!element) {
            alert("Unable to capture report");
            return;
          }

          await convertAllImagesToBase64(element);

          const canvas = await html2canvas(element, {
            scale: 1,
            useCORS: true,
            allowTaint: true,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
          });

          const imageData = canvas.toDataURL("image/png");
          const imageBytes = await fetch(imageData).then((res) =>
            res.arrayBuffer(),
          );

          const pdfDoc = await PDFDocument.create();
          const image = await pdfDoc.embedPng(imageBytes);
          const page = pdfDoc.addPage([image.width, image.height]);

          page.drawImage(image, {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
          });

          const pdfBytes = await pdfDoc.save();
          const blob = new Blob([pdfBytes], {
            type: "application/pdf",
          });

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${reportType}_Report_${studentName.replace(
            /\s+/g,
            "_",
          )}_${Date.now()}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("PDF generation error:", error);
          alert("Failed to generate PDF");
        } finally {
          setPdfGenerating(false);
        }
      },
      generateImage: async () => {
        if (pdfGenerating) return;

        try {
          setPdfGenerating(true);

          const element = targetViewRef.current;
          if (!element) {
            alert("Unable to capture report");
            return;
          }

          await convertAllImagesToBase64(element);

          const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
          });

          const imageData = canvas.toDataURL("image/png", 1.0);
          const fileName = `${reportType}_Report_${studentName.replace(
            /\s+/g,
            "_",
          )}_${Date.now()}.png`;

          const link = document.createElement("a");
          link.download = fileName;
          link.href = imageData;
          link.click();
        } catch (error) {
          console.error(error);
          alert("Failed to generate image");
        } finally {
          setPdfGenerating(false);
        }
      },
    }));

    return (
      <div style={{ position: "relative" }}>
        {children(targetViewRef)}

        {pdfGenerating && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "24px 32px",
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              }}
            >
              <div className="report-spinner" />
              <span
                style={{
                  color: "#2563EB",
                  fontWeight: 600,
                }}
              >
                Downloading...
              </span>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default ScreenShotWrapper;
